// Tela de gestão de usuários (PO1-2019 + PO1-2046; padronizada na PO1-3296).
// Hero próprio (mantido) → indicadores (MetricCardGroup, clicáveis como filtro
// rápido) → FilterBand (busca + último acesso) → ShellCard com a tabela padrão
// (SortHeader + TablePagination), que troca entre usuários e convites pendentes.
// Cores só por AUTOPILOT_COLORS / tokens --everlog-* (tema claro/escuro).

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Row, Col, Card, CardBody, Button, Table, Spinner } from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import { FaUserPlus, FaEnvelope, FaSyncAlt } from "react-icons/fa";

import {
  listUsers,
  setUserActive,
  deleteUser,
  restoreUser,
} from "../../../services/usersApi";
import { listInvites, cancelInvite, resendInvite } from "../../../services/invitesApi";
import { listRoles } from "../../../services/rolesApi";
import { useToast } from "../../../hooks/toast";
import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "../../../hooks/companies";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import FilterBand from "../../../components/FilterBand";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import { paginate, sortRows, toggleSort } from "../../../utils/tableHelpers";
import { reactSelectStyles, reactSelectPortalProps } from "../../../utils/reactSelectStyles";
import {
  AUTOPILOT_COLORS,
  autopilotCardStyle,
  autopilotSecondaryButtonStyle,
} from "../../../components/autopilot/styles";

import EditUserModal from "./components/EditUserModal";
import SetPasswordModal from "./components/SetPasswordModal";
import InviteUsersModal from "./components/InviteUsersModal";

// Indicadores do topo: cada um vira filtro rápido da lista.
const VIEWS = [
  {
    value: "all",
    label: "Todos",
    tone: "info",
    icon: "tim-icons icon-single-02",
    helper: "Usuários cadastrados no embarcador",
  },
  {
    value: "active",
    label: "Ativos",
    tone: "success",
    icon: "tim-icons icon-check-2",
    helper: "Login liberado e senha cadastrada",
  },
  {
    value: "inactive",
    label: "Inativos",
    tone: "danger",
    icon: "tim-icons icon-lock-circle",
    helper: "Bloqueados ou aguardando ativação",
  },
  {
    value: "pending",
    label: "Convites pendentes",
    tone: "warning",
    icon: "tim-icons icon-email-85",
    helper: "Aguardando o convidado aceitar e definir a senha",
  },
  {
    value: "deleted",
    label: "Excluídos",
    tone: "neutral",
    icon: "tim-icons icon-trash-simple",
    helper: "Removidos e ainda restauráveis",
  },
];

const heroCardStyle = {
  ...autopilotCardStyle,
  background: "var(--everlog-card-hero-bg)",
};

// Hero alinhado com ListagemCotacoes/Holidays — title 2rem, eyebrow uppercase
// e descrição com line-height 1.7. Sem CSS dedicado pra evitar arquivo só
// pra essa tela.
const heroBodyStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 24,
  flexWrap: "wrap",
  padding: "1.6rem 1.75rem",
  background: "transparent",
};

const heroIntroStyle = {
  flex: "1 1 560px",
  minWidth: 280,
};

const heroEyebrowStyle = {
  display: "inline-block",
  marginBottom: 10,
  color: "var(--theme-accent)",
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

const heroTitleStyle = {
  margin: "0 0 10px",
  color: "var(--everlog-card-title)",
  fontSize: "2rem",
  fontWeight: 700,
};

const heroDescriptionStyle = {
  maxWidth: 760,
  margin: 0,
  color: "var(--everlog-card-muted)",
  lineHeight: 1.7,
};

const heroActionsStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  flexWrap: "wrap",
};


const fieldLabelStyle = {
  marginBottom: 6,
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.76rem",
  fontWeight: 600,
};

const stateBoxStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  padding: "3rem 1.5rem",
  textAlign: "center",
  color: AUTOPILOT_COLORS.muted,
};

const cellStyle = { verticalAlign: "middle" };
const cellNowrapStyle = { verticalAlign: "middle", whiteSpace: "nowrap" };

// Pílula de status nos tokens do tema (sem hex): sucesso, perigo, aviso, info, neutro.
const PILL_TONES = {
  success: { color: AUTOPILOT_COLORS.statusSuccess, bg: AUTOPILOT_COLORS.statusSuccessBg },
  danger: { color: AUTOPILOT_COLORS.statusDanger, bg: AUTOPILOT_COLORS.statusDangerBg },
  warning: { color: AUTOPILOT_COLORS.statusWarning, bg: AUTOPILOT_COLORS.statusWarningBg },
  info: { color: AUTOPILOT_COLORS.accent, bg: "var(--theme-accent-glow)" },
  neutral: { color: AUTOPILOT_COLORS.muted, bg: AUTOPILOT_COLORS.surfacePill },
};

function StatusPill({ tone, children, title }) {
  const p = PILL_TONES[tone] || PILL_TONES.neutral;
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 600,
        background: p.bg,
        color: p.color,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// Botão-ícone das ações da linha (mesmo padrão de MinhasTransportadoras).
function RowAction({ icon, title, onClick, color, disabled }) {
  return (
    <Button
      type="button"
      color="link"
      size="sm"
      className="btn-icon"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      style={{ color: color || AUTOPILOT_COLORS.accent }}
    >
      <i className={icon} />
    </Button>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}

// Deriva o estado tri-valorado (active/inactive/deleted). Prioriza o campo
// `status` do backend (PO1-2047); cai para isDeleted/isActive por robustez.
function userStatus(u) {
  if (u.status) return u.status;
  if (u.isDeleted) return "deleted";
  return u.isActive ? "active" : "inactive";
}

// Tooltip do pill "Excluído": quem excluiu + quando, quando disponível.
// deletionInfo (deleterUserId/deletionTime) ou os campos crus DeleterUserId/DeletionTime.
function deletionTooltip(u) {
  const info = u.deletionInfo || {};
  const who = info.deleterUserId != null ? info.deleterUserId : u.DeleterUserId;
  const when = info.deletionTime != null ? info.deletionTime : u.DeletionTime;
  if (who == null && when == null) return "Usuário excluído";
  const parts = ["Excluído"];
  if (who != null) parts.push(`por usuário #${who}`);
  if (when != null) parts.push(`em ${formatDate(when)}`);
  return parts.join(" ");
}

// Filtros de período aplicados sobre o último acesso (Hub OU Flow — basta um
// dos dois ter acessado dentro da janela). `never` bate quando ambos são null.
const ACCESS_PERIODS = [
  { value: "any", label: "Qualquer período" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "this-month", label: "Este mês" },
  { value: "stale-30d", label: "Sem acesso há 30+ dias" },
  { value: "never", label: "Nunca acessaram" },
];

// PO1-3296: o acesso ao Flow (sistema antigo) só conta para o admin Everlog;
// para o cliente o recorte usa apenas o último acesso ao Hub.
function matchesAccessPeriod(user, period, incluirFlow = true) {
  if (!period || period === "any") return true;
  const hub = user.lastLoginHubAt ? new Date(user.lastLoginHubAt) : null;
  const flow = incluirFlow && user.lastLoginFlowAt ? new Date(user.lastLoginFlowAt) : null;
  const last = (() => {
    if (hub && flow) return hub > flow ? hub : flow;
    return hub || flow;
  })();

  if (period === "never") return !last;
  if (!last) return false;

  const now = new Date();
  const diffDays = (now - last) / (24 * 3600 * 1000);

  if (period === "7d") return diffDays <= 7;
  if (period === "30d") return diffDays <= 30;
  if (period === "this-month") {
    return (
      last.getFullYear() === now.getFullYear() &&
      last.getMonth() === now.getMonth()
    );
  }
  if (period === "stale-30d") return diffDays > 30;
  return true;
}

const Users = () => {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [busyInvite, setBusyInvite] = useState(null);

  const [view, setView] = useState("all");
  const [search, setSearch] = useState("");
  const [accessPeriod, setAccessPeriod] = useState("any");
  // PO1-3296: filtro por grupo. Opções vêm de GET /roles (para o cliente, só os
  // grupos que ele pode atribuir; para o admin, todos).
  const [grupoFiltro, setGrupoFiltro] = useState(null);
  const [gruposOpcoes, setGruposOpcoes] = useState([]);

  const [passwordTarget, setPasswordTarget] = useState(null);
  const [editingUser, setEditingUser] = useState(undefined);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { addToast } = useToast();
  const { user } = useAuth();
  const { empresa, isAllEmpresas, hasEmpresaSelection, empresaId } = useCompanies();
  const isAdminEverlog = user && user.setor === "admin";

  // Filtro a aplicar nas chamadas. Para admin Everlog em "Todos os
  // embarcadores" → null (backend retorna todos). Para embarcador comum,
  // o backend ignora qualquer valor enviado e força o do JWT, mas mandamos
  // o do contexto pra deixar a intenção explícita.
  const idEmbarcadorFilter = isAllEmpresas ? null : empresaId || null;

  // Carrega quando há uma empresa específica selecionada OU quando o admin
  // Everlog está em "Todos os embarcadores" (idEmbarcadorFilter = null → o
  // backend devolve a base global pra admin).
  const canLoad = hasEmpresaSelection || isAllEmpresas;

  // Só puxamos os excluídos quando a view "Excluídos" está aberta. Como é
  // booleano, a identidade de loadUsers só muda ao cruzar essa fronteira —
  // trocar entre all/active/inactive não refaz o fetch.
  const includeDeleted = view === "deleted";

  const loadUsers = useCallback(async () => {
    if (!canLoad) {
      setUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const data = await listUsers({
        idEmbarcador: idEmbarcadorFilter,
        includeDeleted,
      });
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[Users] loadUsers:", err);
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: "Não foi possível carregar a base de usuários.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [addToast, canLoad, idEmbarcadorFilter, includeDeleted]);

  const loadInvites = useCallback(async () => {
    if (!canLoad) {
      setInvites([]);
      return;
    }
    setLoadingInvites(true);
    try {
      const data = await listInvites({ idEmbarcador: idEmbarcadorFilter });
      setInvites(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error("[Users] loadInvites:", err);
    } finally {
      setLoadingInvites(false);
    }
  }, [canLoad, idEmbarcadorFilter]);

  useEffect(() => {
    loadUsers();
    loadInvites();
  }, [loadUsers, loadInvites]);

  useEffect(() => {
    if (!canLoad) return;
    listRoles()
      .then((data) => {
        const items = (data && data.items) || [];
        setGruposOpcoes(
          items
            .map((r) => ({ value: Number(r.Id), label: r.DisplayName || r.Name }))
            .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
        );
      })
      .catch((err) => console.error("[Users] listRoles:", err));
  }, [canLoad]);

  const handleToggleActive = useCallback(
    async (row) => {
      try {
        await setUserActive(row.id, !row.isActive);
        addToast({
          place: "tr",
          type: "success",
          title: row.isActive ? "Inativado" : "Ativado",
          description: `${row.name} ${row.isActive ? "inativado" : "ativado"}.`,
          icon: "tim-icons icon-check-2",
        });
        loadUsers();
      } catch (err) {
        console.error("[Users] toggleActive:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Não foi possível alterar o status.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      }
    },
    [addToast, loadUsers]
  );

  const handleDelete = useCallback(
    async (row) => {
      const displayName = row.surname ? `${row.name} ${row.surname}` : row.name;
      if (
        !window.confirm(
          `Excluir o usuário ${displayName}? O acesso será revogado. Você poderá restaurá-lo depois pela aba "Excluídos".`
        )
      ) {
        return;
      }
      try {
        await deleteUser(row.id);
        addToast({
          place: "tr",
          type: "success",
          title: "Excluído",
          description: `${row.name} excluído. Restaurável pela aba "Excluídos".`,
          icon: "tim-icons icon-check-2",
        });
        loadUsers();
      } catch (err) {
        console.error("[Users] delete:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Não foi possível excluir o usuário.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      }
    },
    [addToast, loadUsers]
  );

  const handleRestore = useCallback(
    async (row) => {
      try {
        await restoreUser(row.id);
        addToast({
          place: "tr",
          type: "success",
          title: "Restaurado",
          description: `${row.name} restaurado. O login volta a ficar disponível.`,
          icon: "tim-icons icon-check-2",
        });
        loadUsers();
      } catch (err) {
        console.error("[Users] restore:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Não foi possível restaurar o usuário.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      }
    },
    [addToast, loadUsers]
  );

  const handleResendInvite = useCallback(
    async (jti) => {
      setBusyInvite(jti);
      try {
        await resendInvite(jti);
        addToast({
          place: "tr",
          type: "success",
          title: "Convite reenviado",
          icon: "tim-icons icon-email-85",
        });
        loadInvites();
      } catch (err) {
        console.error("[Users] resend:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Falha ao reenviar convite.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setBusyInvite(null);
      }
    },
    [addToast, loadInvites]
  );

  const handleCancelInvite = useCallback(
    async (jti) => {
      setBusyInvite(jti);
      try {
        await cancelInvite(jti);
        addToast({
          place: "tr",
          type: "success",
          title: "Convite cancelado",
          icon: "tim-icons icon-check-2",
        });
        loadInvites();
      } catch (err) {
        console.error("[Users] cancel:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Falha ao cancelar convite.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setBusyInvite(null);
      }
    },
    [addToast, loadInvites]
  );

  // ─── Filtros e contagens ──────────────────────────────────────────────────

  // all/active/inactive sempre desconsideram os excluídos, mesmo quando a
  // lista carregada inclui deletados (view "Excluídos"), pra manter os totais
  // estáveis. `deleted` só reflete um número real quando a view está aberta
  // (é a única que puxa includeDeleted=true).
  const counts = useMemo(
    () => ({
      all: users.filter((u) => userStatus(u) !== "deleted").length,
      active: users.filter((u) => userStatus(u) === "active").length,
      inactive: users.filter((u) => userStatus(u) === "inactive").length,
      pending: invites.length,
      deleted: users.filter((u) => userStatus(u) === "deleted").length,
    }),
    [users, invites]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const status = userStatus(u);
      // Na view "Excluídos" mostramos só os deletados; nas demais, escondemos.
      if (view === "deleted") {
        if (status !== "deleted") return false;
      } else {
        if (status === "deleted") return false;
        if (view === "active" && status !== "active") return false;
        if (view === "inactive" && status !== "inactive") return false;
      }
      if (!matchesAccessPeriod(u, accessPeriod, isAdminEverlog)) return false;
      if (grupoFiltro && !(u.groups || []).some((g) => Number(g.id) === grupoFiltro)) return false;
      if (!q) return true;
      const haystack = [u.name, u.surname, u.userName, u.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [users, view, search, accessPeriod, isAdminEverlog, grupoFiltro]);

  const filteredInvites = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invites;
    return invites.filter((i) => String(i.email || "").toLowerCase().includes(q));
  }, [invites, search]);


  // ─── Ordenação e paginação (client-side) ──────────────────────────────────

  const [userSort, setUserSort] = useState({ field: "name", dir: "asc" });
  const [inviteSort, setInviteSort] = useState({ field: "createdAt", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filtrosOpen, setFiltrosOpen] = useState(false);

  // Volta para a primeira página quando o recorte muda.
  useEffect(() => {
    setPage(1);
  }, [view, search, accessPeriod, grupoFiltro, pageSize]);

  const sortedUsers = useMemo(
    () =>
      sortRows(filteredUsers, userSort, (u, field) => {
        if (field === "name") return `${u.name || ""} ${u.surname || ""}`.trim();
        if (field === "status") return userStatus(u);
        if (field === "groups") return (u.groups || []).map((g) => g.name).join(", ");
        if (field === "lastLoginHubAt" || field === "lastLoginFlowAt") {
          return u[field] ? new Date(u[field]) : null;
        }
        return u[field];
      }),
    [filteredUsers, userSort]
  );

  const sortedInvites = useMemo(
    () =>
      sortRows(filteredInvites, inviteSort, (i, field) =>
        field === "createdAt" || field === "expiresAt" ? (i[field] ? new Date(i[field]) : null) : i[field]
      ),
    [filteredInvites, inviteSort]
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  const showingInvites = view === "pending";
  const totalShown = showingInvites ? filteredInvites.length : filteredUsers.length;
  const totalLabelNoun = showingInvites
    ? totalShown === 1
      ? "convite pendente"
      : "convites pendentes"
    : totalShown === 1
      ? "usuário"
      : "usuários";
  const temRecorte = !!search || accessPeriod !== "any" || !!grupoFiltro;
  const filtrosAtivosCount = (accessPeriod !== "any" ? 1 : 0) + (grupoFiltro ? 1 : 0);
  const limparRecorte = () => {
    setSearch("");
    setAccessPeriod("any");
    setGrupoFiltro(null);
  };

  // Sem empresa selecionada → notice + early return (mesmo padrão do
  // ListagemCotacoes / Holidays). Admin Everlog em "Todos os embarcadores"
  // continua dentro: o backend já aceita listUsers({ idEmbarcador: null })
  // e retorna a base global — necessário para editar admins que não estão
  // atrelados a nenhum embarcador específico.
  if (!hasEmpresaSelection && !isAllEmpresas) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  // Admin Everlog em "Todos os embarcadores" não tem alvo concreto pra
  // convite — desabilita o CTA pra evitar confusão.
  const canInvite = !isAllEmpresas && !!idEmbarcadorFilter;
  const empresaLabel = empresa?.label || empresa?.nome || "embarcador selecionado";
  const viewAtual = VIEWS.find((v) => v.value === view) || VIEWS[0];
  const periodoAtual = ACCESS_PERIODS.find((p) => p.value === accessPeriod) || ACCESS_PERIODS[0];

  const paginaUsuarios = paginate(sortedUsers, page, pageSize);
  const paginaConvites = paginate(sortedInvites, page, pageSize);

  const renderVazio = (icone, titulo, texto, acao) => (
    <div style={stateBoxStyle}>
      <i className={icone} style={{ fontSize: "1.6rem", color: AUTOPILOT_COLORS.muted }} />
      <strong style={{ color: AUTOPILOT_COLORS.title }}>{titulo}</strong>
      <span>{texto}</span>
      {acao}
    </div>
  );

  const botaoConvidar = canInvite ? (
    <Button size="sm" color="info" outline onClick={() => setInviteOpen(true)}>
      Convidar por e-mail
    </Button>
  ) : null;

  const renderUsuarios = () => {
    if (loadingUsers && users.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
          <span>Carregando usuários...</span>
        </div>
      );
    }
    if (!filteredUsers.length) {
      if (temRecorte) {
        return renderVazio(
          "tim-icons icon-zoom-split",
          "Nenhum usuário no recorte atual",
          "Ajuste a busca ou o período de acesso.",
          <Button size="sm" color="info" outline onClick={limparRecorte}>
            Limpar busca e filtros
          </Button>
        );
      }
      const vazio = {
        all: ["Nenhum usuário cadastrado ainda", "Comece convidando os primeiros usuários por e-mail."],
        active: ["Nenhum usuário ativo", "Todos estão bloqueados ou ainda não confirmaram a senha."],
        inactive: ["Nenhum usuário inativo", "Toda a base está com login liberado."],
        deleted: ["Nenhum usuário excluído", "Usuários removidos aparecem aqui e podem ser restaurados."],
      }[view] || ["Nenhum usuário", ""];
      return renderVazio("tim-icons icon-single-02", vazio[0], vazio[1], view !== "deleted" ? botaoConvidar : null);
    }
    const onSort = (f) => setUserSort(toggleSort(userSort, f));
    return (
      <TableSurface>
        <div className="table-responsive">
          <Table size="sm" hover className="hub-thead-accent" style={{ marginBottom: 0 }}>
            <thead className="text-primary">
              <tr>
                <th><SortHeader label="Nome" field="name" sort={userSort} onSort={onSort} /></th>
                <th><SortHeader label="E-mail" field="email" sort={userSort} onSort={onSort} /></th>
                <th><SortHeader label="Grupos" field="groups" sort={userSort} onSort={onSort} /></th>
                <th><SortHeader label="Status" field="status" sort={userSort} onSort={onSort} /></th>
                <th><SortHeader label="Último acesso Hub" field="lastLoginHubAt" sort={userSort} onSort={onSort} /></th>
                {isAdminEverlog && (
                  <th><SortHeader label="Último acesso Flow" field="lastLoginFlowAt" sort={userSort} onSort={onSort} /></th>
                )}
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginaUsuarios.items.map((u) => {
                const status = userStatus(u);
                return (
                  <tr key={u.id}>
                    <td style={cellStyle}>
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>
                          {u.name}
                          {u.surname ? ` ${u.surname}` : ""}
                        </span>
                        <small style={{ color: AUTOPILOT_COLORS.muted }}>@{u.userName}</small>
                      </div>
                    </td>
                    <td style={cellStyle}>{u.email || "—"}</td>
                    <td style={cellStyle}>
                      {(u.groups || []).length ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {u.groups.map((g) => (
                            <StatusPill key={g.id} tone="info">
                              {g.name}
                            </StatusPill>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: AUTOPILOT_COLORS.muted }}>sem grupo</span>
                      )}
                    </td>
                    <td style={cellNowrapStyle}>
                      {status === "deleted" ? (
                        <StatusPill tone="neutral" title={deletionTooltip(u)}>Excluído</StatusPill>
                      ) : status === "active" ? (
                        <StatusPill tone="success">Ativo</StatusPill>
                      ) : (
                        <StatusPill tone="danger">Inativo</StatusPill>
                      )}
                    </td>
                    <td style={cellNowrapStyle}>
                      {u.lastLoginHubAt ? formatDate(u.lastLoginHubAt) : <span style={{ color: AUTOPILOT_COLORS.muted }}>nunca</span>}
                    </td>
                    {isAdminEverlog && (
                      <td style={cellNowrapStyle}>
                        {u.lastLoginFlowAt ? formatDate(u.lastLoginFlowAt) : <span style={{ color: AUTOPILOT_COLORS.muted }}>nunca</span>}
                      </td>
                    )}
                    <td className="text-right" style={cellNowrapStyle}>
                      {status === "deleted" ? (
                        <RowAction icon="tim-icons icon-refresh-01" title="Restaurar" onClick={() => handleRestore(u)} />
                      ) : (
                        <>
                          <RowAction
                            icon={u.isActive ? "tim-icons icon-lock-circle" : "tim-icons icon-check-2"}
                            title={u.isActive ? "Inativar" : "Ativar"}
                            color={u.isActive ? AUTOPILOT_COLORS.statusWarning : AUTOPILOT_COLORS.statusSuccess}
                            onClick={() => handleToggleActive(u)}
                          />
                          <RowAction icon="tim-icons icon-pencil" title="Editar" onClick={() => setEditingUser(u)} />
                          <RowAction icon="tim-icons icon-key-25" title="Definir senha" onClick={() => setPasswordTarget(u)} />
                          <RowAction
                            icon="tim-icons icon-trash-simple"
                            title="Excluir"
                            color={AUTOPILOT_COLORS.statusDanger}
                            onClick={() => handleDelete(u)}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        <TablePagination
          page={paginaUsuarios.page}
          pageSize={pageSize}
          total={sortedUsers.length}
          itemLabel="usuário(s)"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableSurface>
    );
  };

  const renderConvites = () => {
    if (loadingInvites && invites.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
          <span>Carregando convites...</span>
        </div>
      );
    }
    if (!filteredInvites.length) {
      return search
        ? renderVazio(
            "tim-icons icon-zoom-split",
            "Nenhum convite encontrado",
            "Limpe a busca para ver todos os convites em aberto.",
            <Button size="sm" color="info" outline onClick={limparRecorte}>
              Limpar busca
            </Button>
          )
        : renderVazio(
            "tim-icons icon-email-85",
            "Nenhum convite pendente",
            "Convide novos usuários por e-mail; o link de cadastro tem validade.",
            botaoConvidar
          );
    }
    const onSort = (f) => setInviteSort(toggleSort(inviteSort, f));
    return (
      <TableSurface>
        <div className="table-responsive">
          <Table size="sm" hover className="hub-thead-accent" style={{ marginBottom: 0 }}>
            <thead className="text-primary">
              <tr>
                <th><SortHeader label="E-mail" field="email" sort={inviteSort} onSort={onSort} /></th>
                <th><SortHeader label="Enviado em" field="createdAt" sort={inviteSort} onSort={onSort} /></th>
                <th><SortHeader label="Expira em" field="expiresAt" sort={inviteSort} onSort={onSort} /></th>
                <th>Grupos</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginaConvites.items.map((it) => (
                <tr key={it.jti}>
                  <td style={cellStyle}>
                    <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{it.email}</span>
                  </td>
                  <td style={cellNowrapStyle}>{formatDate(it.createdAt)}</td>
                  <td style={cellNowrapStyle}>{formatDate(it.expiresAt)}</td>
                  <td style={cellNowrapStyle}>
                    {it.roleIds && it.roleIds.length > 0 ? (
                      <StatusPill tone="info">
                        {it.roleIds.length} grupo{it.roleIds.length !== 1 ? "s" : ""}
                      </StatusPill>
                    ) : (
                      <StatusPill tone="neutral">sem grupo</StatusPill>
                    )}
                  </td>
                  <td className="text-right" style={cellNowrapStyle}>
                    <RowAction
                      icon="tim-icons icon-send"
                      title="Reenviar convite"
                      disabled={busyInvite === it.jti}
                      onClick={() => handleResendInvite(it.jti)}
                    />
                    <RowAction
                      icon="tim-icons icon-simple-remove"
                      title="Cancelar convite"
                      color={AUTOPILOT_COLORS.statusDanger}
                      disabled={busyInvite === it.jti}
                      onClick={() => handleCancelInvite(it.jti)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <TablePagination
          page={paginaConvites.page}
          pageSize={pageSize}
          total={sortedInvites.length}
          itemLabel="convite(s)"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableSurface>
    );
  };

  return (
    <div className="content">
      <SetPasswordModal
        isOpen={!!passwordTarget}
        user={passwordTarget}
        onClose={() => {
          setPasswordTarget(null);
          loadUsers();
        }}
      />

      <EditUserModal
        isOpen={editingUser !== undefined}
        user={editingUser}
        onClose={() => setEditingUser(undefined)}
        onSaved={loadUsers}
      />

      <InviteUsersModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={loadInvites}
        idEmbarcador={idEmbarcadorFilter}
        embarcadorLabel={empresaLabel}
      />

      {/* HERO */}
      <Card style={heroCardStyle}>
        <CardBody style={heroBodyStyle}>
          <div style={heroIntroStyle}>
            <span style={heroEyebrowStyle}>Acessos</span>
            <h2 style={heroTitleStyle}>Usuários</h2>
            <p style={heroDescriptionStyle}>
              {isAllEmpresas
                ? "Visão consolidada de todos os embarcadores. Selecione um embarcador no header para convidar novos usuários."
                : `Gerencie quem acessa o Hub em nome de ${empresaLabel}: convide novos usuários por e-mail, acompanhe convites pendentes e mantenha a base limpa entre ativos e bloqueados.`}
            </p>
          </div>
          <div style={heroActionsStyle}>
            <Button
              className="btn-sm"
              color="default"
              outline
              onClick={() => {
                loadUsers();
                loadInvites();
              }}
              disabled={loadingUsers || loadingInvites}
              title="Recarregar"
            >
              {loadingUsers || loadingInvites ? (
                <>
                  <ClipLoader size={12} color="currentColor" /> Atualizando...
                </>
              ) : (
                <>
                  <FaSyncAlt /> Atualizar
                </>
              )}
            </Button>
            {isAdminEverlog && (
              <Button
                className="btn-sm"
                color="default"
                outline
                onClick={() => setEditingUser(null)}
                title="Criar usuário manualmente (admin Everlog)"
              >
                <FaUserPlus /> Novo manual
              </Button>
            )}
            <Button
              className="btn-sm"
              style={
                canInvite
                  ? autopilotSecondaryButtonStyle
                  : { ...autopilotSecondaryButtonStyle, opacity: 0.55 }
              }
              onClick={() => setInviteOpen(true)}
              disabled={!canInvite}
              title={
                canInvite
                  ? "Enviar convites por e-mail"
                  : "Selecione um embarcador específico no header para convidar"
              }
            >
              <FaEnvelope /> Convidar por e-mail
            </Button>
          </div>
        </CardBody>
      </Card>

      <MetricCardGroup
        title="Situação da base"
        description="Clique em um indicador para filtrar a lista."
        style={{ marginTop: 16 }}
      >
        {VIEWS.map((v) => (
          <MetricCard
            key={v.value}
            label={v.label}
            value={counts[v.value]}
            helper={v.helper}
            tone={v.tone}
            iconClassName={v.icon}
            active={view === v.value}
            onClick={() => setView(v.value)}
          />
        ))}
      </MetricCardGroup>

      <FilterBand
        mode="client"
        searchLabel={showingInvites ? "Buscar convite" : "Buscar usuário"}
        searchHint={showingInvites ? "E-mail do convidado" : "Nome, usuário ou e-mail"}
        searchPlaceholder={showingInvites ? "Digite o e-mail do convidado" : "Digite o nome, o usuário ou o e-mail"}
        searchValue={search}
        onSearchChange={setSearch}
        activeFiltersCount={filtrosAtivosCount}
        open={filtrosOpen}
        onToggle={setFiltrosOpen}
        panelTitle="Filtros"
        panelDescription={isAdminEverlog ? "Recorte por grupo e pelo último acesso ao Hub ou ao Flow." : "Recorte por grupo e pelo último acesso ao Hub."}
        onClear={temRecorte ? limparRecorte : undefined}
        showActions
        showFiltersButton={!showingInvites}
      >
        <Row>
          <Col md={4}>
            <div style={fieldLabelStyle}>Grupo</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={[{ value: null, label: "Todos os grupos" }].concat(gruposOpcoes)}
              value={gruposOpcoes.find((o) => o.value === grupoFiltro) || { value: null, label: "Todos os grupos" }}
              onChange={(opt) => setGrupoFiltro((opt && opt.value) || null)}
              isSearchable={false}
            />
          </Col>
          <Col md={4}>
            <div style={fieldLabelStyle}>Último acesso</div>
            <Select
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={ACCESS_PERIODS}
              value={periodoAtual}
              onChange={(opt) => setAccessPeriod((opt && opt.value) || "any")}
              isSearchable={false}
            />
          </Col>
        </Row>
      </FilterBand>

      <ShellCard
        title={showingInvites ? "Convites pendentes" : viewAtual.value === "all" ? "Usuários" : `Usuários ${viewAtual.label.toLowerCase()}`}
        description={`${totalShown} ${totalLabelNoun} ${temRecorte ? "no recorte atual" : "no total"}`}
        style={{ marginTop: 16 }}
      >
        {showingInvites ? renderConvites() : renderUsuarios()}
      </ShellCard>
    </div>
  );
};

export default Users;
